import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Select } from "../../lib/ui.js";
import { SectionCard, errorStyle, mutedStyle } from "./common.js";

interface Choice {
  id: string;
  name: string;
  online_bookable: boolean;
}

interface WebBooking {
  offered: boolean;
  enabled: boolean;
  slug: string | null;
  min_notice_minutes: number;
  max_days_ahead: number;
  change_notice_hours: number;
  services: Choice[];
  resources: Choice[];
}

const NOTICE_CHOICES = [
  { minutes: 0, label: "No notice needed" },
  { minutes: 60, label: "1 hour" },
  { minutes: 120, label: "2 hours" },
  { minutes: 240, label: "4 hours" },
  { minutes: 1440, label: "1 day" },
  { minutes: 2880, label: "2 days" },
];
const AHEAD_CHOICES = [14, 30, 60, 90, 180];
const CHANGE_CHOICES = [
  { hours: 0, label: "Right up to the booking" },
  { hours: 2, label: "2 hours before" },
  { hours: 4, label: "4 hours before" },
  { hours: 12, label: "12 hours before" },
  { hours: 24, label: "1 day before" },
  { hours: 48, label: "2 days before" },
];

const ERRORS: Record<string, string> = {
  web_booking_not_available: "Online booking is not switched on for your business.",
  web_booking_nothing_bookable: "Choose at least one service and one staff member customers can book.",
  invalid_web_booking_settings: "One of the settings is out of range.",
};

const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 5, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" };

/**
 * Online booking is optional twice over: the platform admin offers it to a business (Admin > Online booking), then the owner turns it on
 * here and chooses what customers may book. A business that has not been offered it just sees a note.
 */
export function WebBookingSection() {
  const { activeBusinessId, memberships, isPlatformAdmin } = useAuth();
  const isOwner = isPlatformAdmin || memberships.some((m) => m.business_id === activeBusinessId && m.role === "owner");
  const [saved, setSaved] = useState<WebBooking | null>(null);
  const [form, setForm] = useState<WebBooking | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"link" | "code" | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase.rpc("rpc_portal_get_web_booking", { p_business_id: activeBusinessId }).then(({ data }) => {
      if (data) {
        setSaved(data as WebBooking);
        setForm(data as WebBooking);
      }
    });
  }, [activeBusinessId]);

  if (!form || !saved) return null;

  if (!form.offered) {
    return (
      <SectionCard title="Online booking" description="Let customers book, change and cancel on a web page, with their mobile number checked by a text.">
        <p style={mutedStyle}>Online booking is not switched on for your business. Contact us if you would like it.</p>
      </SectionCard>
    );
  }

  const link = `${window.location.origin}/book/${form.slug}`;
  const snippet = `<script src="${window.location.origin}/embed.js" data-business="${form.slug}"></script>`;
  const changed = JSON.stringify(form) !== JSON.stringify(saved);
  const disabled = !isOwner;

  const update = (patch: Partial<WebBooking>) => {
    setJustSaved(false);
    setForm({ ...form, ...patch });
  };
  const toggle = (list: "services" | "resources", id: string, on: boolean) =>
    update({ [list]: form[list].map((c) => (c.id === id ? { ...c, online_bookable: on } : c)) } as Partial<WebBooking>);

  async function copy(text: string, which: "link" | "code") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Copying is a convenience; the text is selectable on screen.
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !form) return;
    setError(null);
    setJustSaved(false);
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_set_web_booking", {
      p_business_id: activeBusinessId,
      p_enabled: form.enabled,
      p_min_notice_minutes: form.min_notice_minutes,
      p_max_days_ahead: form.max_days_ahead,
      p_change_notice_hours: form.change_notice_hours,
      p_service_ids: form.services.filter((s) => s.online_bookable).map((s) => s.id),
      p_resource_ids: form.resources.filter((r) => r.online_bookable).map((r) => r.id),
    });
    setBusy(false);
    if (err) {
      setError(ERRORS[err.message] ?? err.message);
      return;
    }
    setSaved(form);
    setJustSaved(true);
  }

  return (
    <SectionCard
      title="Online booking"
      description="Customers choose a service and a time, check their mobile with a texted code (new customers also give their name) and the booking appears with your phone bookings. Cancelling or moving a booking always needs a new code."
    >
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "default" : "pointer" }}>
          <input type="checkbox" checked={form.enabled} disabled={disabled} onChange={(e) => update({ enabled: e.target.checked })} style={{ marginTop: 3 }} />
          <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Accept online bookings</span>
            <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>While this is off the booking page shows nothing.</span>
          </span>
        </label>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>Your booking page</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: 13.5, color: "var(--rc-link)", wordBreak: "break-all" }}>{link}</a>
            <Button type="button" onClick={() => void copy(link, "link")}>{copied === "link" ? "Copied" : "Copy link"}</Button>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>
            To show the booking form inside your own website, paste this where it should appear:
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <code style={{ fontSize: 12, padding: "6px 8px", background: "var(--rc-surface-sunken)", borderRadius: 6, wordBreak: "break-all", flex: "1 1 320px" }}>{snippet}</code>
            <Button type="button" onClick={() => void copy(snippet, "code")}>{copied === "code" ? "Copied" : "Copy"}</Button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label style={{ ...labelStyle, minWidth: 200 }}>
            Notice needed for a new booking
            <Select value={form.min_notice_minutes} disabled={disabled} onChange={(e) => update({ min_notice_minutes: Number(e.target.value) })}>
              {NOTICE_CHOICES.map((c) => (
                <option key={c.minutes} value={c.minutes}>{c.label}</option>
              ))}
            </Select>
          </label>
          <label style={{ ...labelStyle, minWidth: 200 }}>
            How far ahead customers can book
            <Select value={form.max_days_ahead} disabled={disabled} onChange={(e) => update({ max_days_ahead: Number(e.target.value) })}>
              {AHEAD_CHOICES.map((d) => (
                <option key={d} value={d}>{d} days</option>
              ))}
            </Select>
          </label>
          <label style={{ ...labelStyle, minWidth: 200 }}>
            Customers can cancel or change online
            <Select value={form.change_notice_hours} disabled={disabled} onChange={(e) => update({ change_notice_hours: Number(e.target.value) })}>
              {CHANGE_CHOICES.map((c) => (
                <option key={c.hours} value={c.hours}>{c.label}</option>
              ))}
            </Select>
          </label>
        </div>

        <ChoiceList title="Services customers can book" items={form.services} disabled={disabled} onToggle={(id, on) => toggle("services", id, on)} />
        <ChoiceList title="Staff customers can book with" items={form.resources} disabled={disabled} onToggle={(id, on) => toggle("resources", id, on)} />

        {disabled ? (
          <p style={mutedStyle}>Only an owner of the business can change these.</p>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Button type="submit" variant="primary" disabled={busy || !changed}>{busy ? "Saving..." : "Save online booking settings"}</Button>
            {justSaved && <span style={{ fontSize: 13, color: "var(--rc-success)" }}>Saved.</span>}
          </div>
        )}
        {error && <p style={errorStyle}>{error}</p>}
      </form>
    </SectionCard>
  );
}

function ChoiceList({ title, items, disabled, onToggle }: { title: string; items: Choice[]; disabled: boolean; onToggle: (id: string, on: boolean) => void }) {
  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
      <legend style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", padding: 0, marginBottom: 6 }}>{title}</legend>
      {items.length === 0 && <span style={mutedStyle}>None yet.</span>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
        {items.map((c) => (
          <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5, minWidth: 180 }}>
            <input type="checkbox" checked={c.online_bookable} disabled={disabled} onChange={(e) => onToggle(c.id, e.target.checked)} />
            {c.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
